import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Colors } from 'planoplan-ui-kit/core/colors';
import { Button } from 'planoplan-ui-kit/core/button';
import { useTranslations } from '@libs/translations';
import { Points } from '@styles';
import { returnDayAndDate } from '../helpers';
import { diffFromMoscowTime } from '../../Promotions-Page/helpers';
import { LazyVimeo } from '@components/Lazy-Vimeo/lazy-vimeo';

const Calendar = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path
      d="M12.6667 2.66669H3.33333C2.59695 2.66669 2 3.26364 2 4.00002V13.3334C2 14.0697 2.59695 14.6667 3.33333 14.6667H12.6667C13.403 14.6667 14 14.0697 14 13.3334V4.00002C14 3.26364 13.403 2.66669 12.6667 2.66669Z"
      stroke="#2B35EE"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M11.0078 1.17078V4.17078" stroke="#2B35EE" strokeLinejoin="round" />
    <path d="M5.00781 1.17078V4.17078" stroke="#2B35EE" strokeLinejoin="round" />
    <path d="M2 6.66669H14" stroke="#2B35EE" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Card = ({ data, today, key, windowWidth }) => {
  const translations = useTranslations();
  const imageWrapperRef = useRef(null);
  const [iframeSize, setIframeSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (imageWrapperRef.current) {
      const iframe = imageWrapperRef.current.offsetWidth;
      const iframeHeight = (iframe * 9) / 16;
      setIframeSize({ width: iframe, height: iframeHeight });
    }
  }, [windowWidth]);

  return (
    <View key={key}>
      <ImgWrapper ref={imageWrapperRef}>
        {data.type === 'live' ? (
          <Img src={data.img} alt={data.title} />
        ) : (
          <LazyVimeo
            videoId={data.link}
            autoplay={false}
            width={iframeSize.width}
            height={iframeSize.height}
            bgImage={data.img}
            controls={1}
            muted={0}
            loop={0}
          />
        )}
      </ImgWrapper>
      <Content>
        <Tag isRegistrationOpen={diffFromMoscowTime(data.date, today) !== 0 && data.type === 'live'} type={data.type}>
          {data.type === 'record'
            ? translations.t('site.webinars.label.recording')
            : diffFromMoscowTime(data.date, today) === 0
            ? translations.t('site.webinars.label.reg_close')
            : translations.t('site.webinars.label.reg_open')}
        </Tag>
        <Title dangerouslySetInnerHTML={{ __html: data.title }} />
        {data.date.length > 0 && data.type === 'live' && (
          <DateLine>
            <Calendar />
            <p>{`${returnDayAndDate(data.date)}, ${data.time} ${translations.t('site.webinars.timeZone')}`}</p>
          </DateLine>
        )}
        <Description type={data.type} dangerouslySetInnerHTML={{ __html: data.description }} />
        {data.type === 'live' && (
          <Button
            href={data.webinar_link}
            target="_blank"
            size={windowWidth <= 1024 ? 'S' : windowWidth >= 1920 ? 'L' : 'M'}
            disabled={diffFromMoscowTime(data.date, today) === 0}>
            {translations.t('site.webinars.regButton')}
          </Button>
        )}
      </Content>
    </View>
  );
};

const View = styled.article`
  display: flex;
  flex-direction: column;
  gap: 10px;
  border: 1px solid #e1e1e1;

  @media (hover: hover) {
    &:hover {
      border: 1px solid ${Colors.blue_300};
    }
  }

  @media ${Points.tablet_landscape} {
    flex-direction: row;
    gap: 20px;
    min-height: 268px;
  }

  @media ${Points.laptop_wide} {
    width: 100%;
    height: 356px;
  }

  @media ${Points.fullhd} {
    height: 386px;
  }
`;

const ImgWrapper = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  overflow: hidden;

  & svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 33px;
    height: 33px;
    transition: 0.2s scale;

    @media (hover: hover) {
      &:hover {
        scale: 1.07;
      }
    }
  }

  @media ${Points.tablet_landscape} {
    max-width: 50%;
    height: auto;
    flex-shrink: 0;

    svg {
      width: 45px;
      height: 45px;
    }
  }

  @media ${Points.laptop_wide} {
    width: 629px;

    svg {
      width: 60px;
      height: 60px;
    }
  }

  @media ${Points.fullhd} {
    width: 680px;
    svg {
      width: 65px;
      height: 65px;
    }
  }
`;

const Img = styled.img`
  width: 100%;
  height: auto;

  @media ${Points.tablet_landscape} {
    width: auto;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  height: 345px;

  @media ${Points.tablet_landscape} {
    height: auto;
    flex-grow: 1;
  }

  @media ${Points.laptop_wide} {
    width: auto;
    max-width: 630px;
  }

  @media ${Points.fullhd} {
    max-width: 680px;
  }
`;

const Tag = styled.div`
  width: fit-content;
  padding: 5px 10px 5px;
  margin-bottom: 10px;
  color: ${Colors.white};
  background-color: ${(p) => (p.isRegistrationOpen ? '#09AC00' : Colors.dark_500)};
  border-radius: 15px;
  font-size: 14px;
  line-height: 1.2;

  @media ${Points.fullhd} {
    font-size: 16px;
    line-height: 1.1;
  }
`;

const Title = styled.h2`
  margin-bottom: 5px;
  font-size: 20px;
  line-height: 1.2;
  font-weight: 300;

  @media ${Points.laptop_wide} {
    font-size: 24px;
    line-height: 1.1;
  }

  @media ${Points.fullhd} {
    font-size: 30px;
    line-height: 1.1;
  }
`;

const DateLine = styled.div`
  display: flex;
  gap: 5px;
  color: ${Colors.main_blue};
  font-size: 14px;
  line-height: 1.2;

  & svg {
    width: 16px;
    height: 16px;
  }

  @media ${Points.laptop_wide} {
    font-size: 16px;
  }
`;

const Description = styled.div`
  margin-top: auto;
  margin-bottom: ${(p) => p.type === 'live' && '20px'};
  color: ${Colors.graphite};
  font-size: 16px;
  line-height: 1.3;
  font-weight: 400;

  @media ${Points.fullhd} {
    font-size: 18px;
    line-height: 1.3;
  }
`;
